import { Controller } from "@hotwired/stimulus";
import Customization from "./customization";
import { misc } from "./customization/category";
import { fileToBase64 } from "./customization/lib";
import CustomizationRequest from "./customization/request";
import { LogoUploadTabManager } from "../helpers/logo_upload_tab_manager.js";

const FILE_SIZE_LIMIT_IN_BYTES = {
  PUBLIC: 512000,
  CMS: 5242880,
};

// Connects to data-controller="site-builder--brand"
export default class extends Controller {
  static targets = [
    "logo",
    "firstName",
    "email",
    "phone",
    "company",
    "tagline",
  ];

  initialize() {
    this.tabItem = document.getElementById("for-brand");
    this.persistedSite = document.body.dataset.siteBuilderSite;
    if (this.persistedSite) {
      this.persistenceController =
        this.application.getControllerForElementAndIdentifier(
          document.querySelector(".ctas"),
          "site-builder--persistence"
        );
    }
  }

  connect() {
    /*
      These values are not displayed on the preview. That is why they are only initialized here
     */

    if (!this.persistedSite) {
      this.assignFormFields();
    } else {
      if (!document.querySelector(".brand-logo img").src) {
        document.querySelector(".brand-logo").style = "display: none";
        this.#toggleLogoControl(false);
      }
    }
  }

  async handleChangeImage(event) {
    event.preventDefault();
    const newLogoURL = event.target.dataset.url;
    await CustomizationRequest.turboFetch(newLogoURL);

    document.activeElement.blur();
    const dialog = document.querySelector('.modal');
    dialog.classList.add("open");
  }

  async updateLogo(logoData) {
    let logoURL = "";
    let logoFileName = "";

    if (this.persistedSite) {
      // Handle base64 or other URL directly
      if (logoData.url) {
        logoURL = logoData.url;
      }
      logoFileName = logoData.name;
    } else {
      const logo = this.logoTarget.files?.[0];

      if (!logo) {
        this.logoTarget.value = "";
        return;
      }

      if (!logo.size) {
        alert(
          "File upload failed. Files must meet dimension, file size, and type requirements."
        );
        this.logoTarget.value = "";
        return;
      }

      if (logo.size > FILE_SIZE_LIMIT_IN_BYTES.PUBLIC) {
        this.logoTarget.value = "";
        alert("Please use logo with size less than 500kb");
        return;
      }
      logoURL = await fileToBase64(logo);
    }

    // Escaping special characters in the filename
    const sanitizedFileName = logoFileName.replace(/[^\w.]/gi, "-");
    const logoObj = {
      url: logoURL,
      smallUrl: logoData.smallUrl,
      id: logoData.id || "",
      name: sanitizedFileName,
    };

    this.setLogo(logoObj);

    const fileNameElement = document.querySelector(".file-name");
    if (fileNameElement) {
      fileNameElement.textContent = sanitizedFileName;
    }
  }

  removeLogo() {
    this.setLogo("");
    this.#enableSubmit();
    this.logoTarget.value = "";
    this.#removeLogoLiquid();
  }

  setLogo(logo) {
    const logoURL = logo?.url ?? logo;
    const smallLogoURL = logo?.smallUrl || logoURL;
    const displayStyle = logoURL ? "block" : "none";

    document.querySelector(".brand-logo").style.display = displayStyle;
    this.#toggleLogoControl(!!logoURL);

    if (logo) {
      document.querySelector(".brand-logo img").src = smallLogoURL;
      delete logo.smallUrl;
      Customization.localUpdate({ logo });
      this.#highlightSelected();
      this.#enableSubmit();
    } else {
      Customization.localUpdate({ logo: "" });
    }
  }

  updateText(event) {
    const { name, value } = event.currentTarget;
    Customization.localUpdate({ [name]: value.trim() });
    this.#highlightSelected();
    this.#enableSubmit();
  }

  reset() {
    Customization.reset({ misc: true });
    this.#removeLogoLiquid();
    this.assignFormFields();
    this.tabItem.classList.remove("has-customization");
  }

  assignFormFields() {
    const values = misc.values;
    const { logo } = misc.values;
    let hasValues = false;
    this.constructor.targets.forEach((target) => {
      const hasTargetKey = `has${target.charAt(0).toUpperCase() + target.slice(1)
        }Target`;
      if (this[hasTargetKey]) {
        if (target === "logo") {
          this[`${target}Target`].value = "";
          this.setLogo(logo);
          if (logo) {
            hasValues = true;
          }
        } else {
          this[`${target}Target`].value =
            values[this[`${target}Target`].name] || "";
          if (values[target]) {
            hasValues = true;
          }
        }
      }
    });
    if (hasValues) {
      this.#highlightSelected();
    }
  }

  #enableSubmit() {
    this.persistenceController?.updateButtonState();
  }

  #removeLogoLiquid() {
    Customization.serverUpdate({ logo: "" });
  }

  #highlightSelected() {
    this.tabItem.classList.add("has-customization");
  }

  #toggleLogoControl(show) {
    const logoControl = document.querySelector(".logo-control");
    const fileUploadPlaceholder = document.querySelector(
      ".file-upload-placeholder"
    );

    if (!logoControl || !fileUploadPlaceholder) return;

    if (show) {
      logoControl.classList.remove("hidden");
      logoControl.classList.add("flex-display");
      fileUploadPlaceholder.classList.add("hidden");
      fileUploadPlaceholder.classList.remove("flex-display");
    } else {
      logoControl.classList.add("hidden");
      logoControl.classList.remove("flex-display");
      logoControl.querySelector(".brand-logo img").removeAttribute("src")
      fileUploadPlaceholder.classList.remove("hidden");
      fileUploadPlaceholder.classList.add("flex-display");
    }
  }

  async handleLogoClick(event) {
    event.preventDefault();

    const hiddenDiv = event.currentTarget.querySelector('.logo-cropper-details');
    if (!hiddenDiv) return;

    const cropperDetails = JSON.parse(hiddenDiv.getAttribute('data-cropper-details'));
    const zoom = cropperDetails.zoom;
    const largerImageUrl = hiddenDiv.getAttribute('data-large-image-url');
    const originalImageName = hiddenDiv.getAttribute('data-original-image-name');
    const activeTab = hiddenDiv.getAttribute('data-active-tab');
    const newLogoURL = `/logos/new?site_id=${this.persistedSite}&edit_cropper=true`;
    await CustomizationRequest.turboFetch(newLogoURL);

    setTimeout(() => {
      LogoUploadTabManager.switchToTab(activeTab, {
        largerImageUrl,
        cropperDetails,
        originalImageName,
        zoom,
        onControllerReady: (logoController) => {
          // Restore the image URLs to thumbImgTarget for library logos
          if ((activeTab === 'media-library' || activeTab === 'media_library') && logoController.thumbImgTarget) {
            const thumbImg = logoController.thumbImgTarget;

            if (largerImageUrl) {
              thumbImg.setAttribute('data-large-url', largerImageUrl);
              thumbImg.setAttribute('src', largerImageUrl);

              // Derive other URLs from the larger image URL
              let baseUrl = largerImageUrl;
              let originalUrl = largerImageUrl;

              if (largerImageUrl.includes('/large/')) {
                baseUrl = largerImageUrl.replace('/large/', '/small/');
                originalUrl = largerImageUrl.replace('/large/', '/original/');
              }

              thumbImg.setAttribute('data-small-url', baseUrl);
              thumbImg.setAttribute('data-original-url', originalUrl);
            }
          }

          LogoUploadTabManager.updateFileName(originalImageName);
          LogoUploadTabManager.openCropper(logoController, {
            largerImageUrl,
            cropperDetails,
            originalImageName,
            zoom
          });

          document.activeElement.blur();
          const dialog = document.querySelector('.modal');
          dialog.classList.add("open");
        }
      });
    }, 500);
  }
}
